import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/admin.js';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const FALLBACK_TIMEZONES = ['UTC', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Tokyo', 'Europe/London', 'America/New_York', 'America/Los_Angeles'];
const FALLBACK_CURRENCIES = ['IDR', 'USD', 'EUR', 'GBP', 'SGD', 'JPY', 'AUD'];

// Keys this form edits. Everything else already stored on the org is carried
// through untouched on save, so nothing is lost by moving off the JSON editor.
const KNOWN_KEYS = ['logo_url', 'timezone', 'currency', 'country', 'fiscal_start_month'];

function supported(kind, fallback) {
  try {
    const v = Intl.supportedValuesOf?.(kind);
    if (v?.length) return v;
  } catch { /* older browsers */ }
  return fallback;
}

// ISO 3166 regions as [code, English name], from the browser's own locale data.
// Empty when Intl.DisplayNames is unavailable; the form then falls back to a text input.
function regionList() {
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    const out = [];
    for (let a = 65; a <= 90; a++) {
      for (let b = 65; b <= 90; b++) {
        const code = String.fromCharCode(a, b);
        const name = dn.of(code);
        if (name && name !== code && code !== 'ZZ') out.push([code, name]);
      }
    }
    return out.sort((x, y) => x[1].localeCompare(y[1]));
  } catch { return []; }
}

function withCurrent(list, current) {
  return current && !list.includes(current) ? [current, ...list] : list;
}

function toForm(org) {
  const s = org?.settings || {};
  return {
    name: org?.name || '',
    logo_url: s.logo_url || '',
    timezone: s.timezone || '',
    currency: s.currency || '',
    country: s.country || '',
    fiscal_start_month: s.fiscal_start_month ? String(s.fiscal_start_month) : '',
  };
}

export default function OrgSettings() {
  const [orgs, setOrgs] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(toForm(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminApi.organizations()
      .then((d) => {
        const items = d?.items || [];
        setOrgs(items);
        if (items[0]) { setActive(items[0]); setForm(toForm(items[0])); }
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load organizations'))
      .finally(() => setLoading(false));
  }, []);

  const timezones = useMemo(() => {
    const all = supported('timeZone', FALLBACK_TIMEZONES);
    return withCurrent(all.includes('UTC') ? all : ['UTC', ...all], form.timezone);
  }, [form.timezone]);
  const currencies = useMemo(() => withCurrent(supported('currency', FALLBACK_CURRENCIES), form.currency), [form.currency]);
  const currencyName = useMemo(() => {
    try { return new Intl.DisplayNames(['en'], { type: 'currency' }); } catch { return null; }
  }, []);

  const regions = useMemo(regionList, []);
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(active));

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function switchOrg(id) {
    const o = orgs.find((x) => x.id === id);
    if (!o) return;
    setActive(o); setForm(toForm(o)); setInfo(null); setError(null);
  }

  async function save(e) {
    e.preventDefault();
    setInfo(null); setError(null);

    // Start from what's stored so unknown keys survive, then apply the form.
    const settings = { ...(active.settings || {}) };
    const trimmed = {
      logo_url: form.logo_url.trim(),
      timezone: form.timezone,
      currency: form.currency,
      country: form.country.trim().toUpperCase(),
      fiscal_start_month: form.fiscal_start_month ? Number(form.fiscal_start_month) : '',
    };
    for (const k of KNOWN_KEYS) {
      if (trimmed[k] === '') delete settings[k];
      else settings[k] = trimmed[k];
    }

    setSaving(true);
    try {
      const r = await adminApi.updateOrg(active.id, { name: form.name.trim(), settings });
      setActive(r.organization);
      setOrgs((list) => list.map((o) => (o.id === r.organization.id ? r.organization : o)));
      setForm(toForm(r.organization));
      setInfo('Saved');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="muted">Loading…</div>;
  if (!active) return <div className="muted">{error || 'No organization configured.'}</div>;

  const extra = Object.entries(active.settings || {}).filter(([k]) => !KNOWN_KEYS.includes(k));

  return (
    <section>
      <header className="page-header"><h1>Organization Settings</h1></header>
      {error && <div className="alert error" role="alert">{error}</div>}
      {info && <div className="alert info" role="status">{info}</div>}
      <form className="card form-card grid-form" style={{ maxWidth: 720 }} onSubmit={save} data-testid="org-settings-form">
        {orgs.length > 1 && (
          <label className="full">Organization
            <select value={active.id} onChange={(e) => switchOrg(e.target.value)}>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}
        <label className="full">Name
          <input value={form.name} onChange={set('name')} required maxLength={255} />
        </label>
        <label className="full">Logo URL
          <input type="url" value={form.logo_url} onChange={set('logo_url')} placeholder="https://example.com/logo.png" />
        </label>
        <label>Timezone
          <select value={form.timezone} onChange={set('timezone')}>
            <option value="">Not set</option>
            {timezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </label>
        <label>Currency
          <select value={form.currency} onChange={set('currency')}>
            <option value="">Not set</option>
            {currencies.map((c) => <option key={c} value={c}>{currencyName?.of(c) ? `${c} — ${currencyName.of(c)}` : c}</option>)}
          </select>
        </label>
        <label>Country
          {regions.length ? (
            <select value={form.country} onChange={set('country')}>
              <option value="">Not set</option>
              {(regions.some(([c]) => c === form.country) || !form.country ? regions : [[form.country, form.country], ...regions])
                .map(([code, name]) => <option key={code} value={code}>{name} ({code})</option>)}
            </select>
          ) : (
            <input value={form.country} onChange={set('country')} maxLength={2} pattern="[A-Za-z]{2}" placeholder="ID" title="Two-letter ISO 3166 country code" />
          )}
        </label>
        <label>Fiscal year starts in
          <select value={form.fiscal_start_month} onChange={set('fiscal_start_month')}>
            <option value="">Not set</option>
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
        </label>
        {extra.length > 0 && (
          <div className="full">
            <div className="muted small">Other stored settings (kept as-is)</div>
            <ul className="chips" style={{ marginTop: 6 }}>
              {extra.map(([k, v]) => (
                <li key={k} className="chip">{k}: {typeof v === 'object' ? JSON.stringify(v) : String(v)}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="actions full">
          <button type="submit" className="btn primary" disabled={saving || !dirty || !form.name.trim()}>{saving ? 'Saving…' : 'Save'}</button>
          <button type="button" className="btn ghost" disabled={saving || !dirty} onClick={() => { setForm(toForm(active)); setInfo(null); setError(null); }}>Reset</button>
        </div>
      </form>
    </section>
  );
}
